import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, setAccessToken, clearAccessToken, renewSession } from "../lib/api";
import { toast } from "sonner";
import { setOwlAgeGroup } from "../lib/owlVoice";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Ajusta la voz del Búho (ElevenLabs) según la edad/rol del usuario activo.
  useEffect(() => {
    if (user) setOwlAgeGroup(user.age, user.role);
  }, [user?.id, user?.age, user?.role]);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch (e) {
      // v5.7.4 · solo un 401/403 significa «no hay sesión». Un fallo de red o del servidor ocupado
      // (502/520/timeout) ya NO te saca de la plataforma: conservamos al usuario actual.
      const st = e?.response?.status;
      if (st === 401 || st === 403) setUser(null);
      return null;
    }
  }, []);

  // Sesión terminada de verdad (la renovación también fue rechazada)
  useEffect(() => {
    const h = () => {
      clearAccessToken();
      setUser((u) => { if (u) toast.info("Tu sesión terminó. Vuelve a ingresar."); return null; });
    };
    window.addEventListener("jasq:session-expired", h);
    return () => window.removeEventListener("jasq:session-expired", h);
  }, []);

  // Mantener la sesión viva mientras se usa la plataforma (renovación cada 30 min, sin molestar)
  useEffect(() => {
    if (!user?.id) return undefined;
    const iv = setInterval(() => { renewSession().catch(() => {}); }, 30 * 60 * 1000);
    return () => clearInterval(iv);
  }, [user?.id]);

  useEffect(() => {
    (async () => {
      await fetchMe();
      setLoading(false);
    })();
  }, [fetchMe]);

  const login = async (email, password) => {
    const attempt = () => api.post("/auth/login", { email, password });
    try {
      let res;
      try { res = await attempt(); }
      catch (e0) {
        // v5.7.4 · un único reintento si el servidor no respondió (red / 502-504 / 52x): no cuenta como contraseña incorrecta
        const st0 = e0?.response?.status;
        if (!e0?.response || st0 === 502 || st0 === 503 || st0 === 504 || (st0 >= 520 && st0 <= 524)) {
          await new Promise((r) => setTimeout(r, 1500));
          res = await attempt();
        } else throw e0;
      }
      const { data } = res;
      // Bearer-token fallback para Safari ITP (Safari descarta cookies
      // SameSite=None en requests cross-site posteriores al login).
      if (data?.access_token) setAccessToken(data.access_token);
      setUser(data);
      return { ok: true, user: data };
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code) {
        return { ok: false, detail, error: detail.message || "Error" };
      }
      const st = e.response?.status;
      if (!e.response) return { ok: false, error: "No se pudo conectar con el servidor. Revisa tu internet e inténtalo de nuevo en unos segundos." };
      if (st >= 500) return { ok: false, error: "El servidor está ocupado en este momento. Espera unos segundos e inténtalo de nuevo (no es tu contraseña)." };
      return { ok: false, error: formatApiErrorDetail(detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearAccessToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, loading, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
